/**
 * Spatial categorical chart. Markers at `encoding.xKey`/`encoding.yKey`, split
 * into one trace per category found in the data's `encoding.groupKey`. Category
 * colors come from `groupColors`, falling back to the palette. The category set
 * always comes from the data; `groupColors` only colors — consistent with box
 * and pie. This is the general grouping mechanism applied to a spatial scatter.
 */

import type { ChartTypeDefinition, ChartTrace } from '../types';
import { registerChart } from '../registry';
import { colorAt } from '../theme/palette';
import { uniqueValues } from '../shared/uniqueValues';
import { xColumn } from '../shared/encoding';
import { figureScale } from '../theme/scale';
import { DEFAULT_MARKER_SIZE } from '../theme/layout';
import { dataExtentRange } from '../shared/layout';
import { toFiniteNumber } from '../shared/number';
import type { NormalizedChartModel } from '../model';
import type { RenderContext } from '../types';
import type { DataPoint } from '../../types/dashboard';

/**
 * Grid pitch along `key`: the smallest positive gap between the distinct values,
 * over the rows given. Gaps under `1e-9 × span` are float noise in centroids built as
 * `origin + i × size`, not a real gap. Undefined with fewer than two distinct values.
 *
 * Memoised per (rows, key) on array identity, like `dataExtentRange`: the rows are the
 * whole dataset, rebuilt rather than mutated, and a 60k-block sort is not free.
 */
const pitchCache = new WeakMap<DataPoint[], Map<string, number | undefined>>();

function gridPitch(rows: DataPoint[], key: string): number | undefined {
  let cached = pitchCache.get(rows);
  if (cached?.has(key)) return cached.get(key);
  const values: number[] = [];
  for (const row of rows) {
    const n = toFiniteNumber(row[key]);
    if (n !== null) values.push(n);
  }
  values.sort((a, b) => a - b);
  const tolerance = values.length > 1 ? (values[values.length - 1] - values[0]) * 1e-9 : 0;
  let pitch: number | undefined;
  for (let i = 1; i < values.length; i++) {
    const gap = values[i] - values[i - 1];
    if (gap > tolerance && (pitch === undefined || gap < pitch)) pitch = gap;
  }
  if (!cached) {
    cached = new Map();
    pitchCache.set(rows, cached);
  }
  cached.set(key, pitch);
  return pitch;
}

/**
 * On-screen side in px of one grid cell, or undefined when it cannot be known yet
 * (plot area not measured, or no second coordinate on either axis). Per axis:
 * pitch × measured axis length ÷ the span that axis shows — the live zoom when the
 * user has one, else the same padded whole-dataset extent the equalAspect layout
 * authors. The smaller axis wins: a square marker must fit both ways.
 *
 * Exact under `constrain: 'domain'`, which honours both ranges. Under the 'range'
 * fallback Plotly widens ONE axis' range to reach 1:1, which overstates that axis'
 * px — but the 1:1 means the other axis is exact, and on a square grid (equal
 * pitches) the minimum picks it. That argument does not hold for a non-square grid,
 * where the overstated axis can be the binding one — see SPEC/2720 "Known
 * limitations". The box itself is re-measured after every draw (ChartRenderer's
 * `measurePlotArea`), so a stale one lasts a single frame.
 */
function cellSidePx(model: NormalizedChartModel, ctx: RenderContext, yKey: string): number | undefined {
  const rows = model.unfilteredData ?? model.data;
  const axes = [
    { key: model.encoding.xKey, axis: 'xaxis', length: ctx.plotArea?.width },
    { key: yKey, axis: 'yaxis', length: ctx.plotArea?.height },
  ];
  let side: number | undefined;
  for (const { key, axis, length } of axes) {
    if (!key || !length || length <= 0) return undefined;
    const pitch = gridPitch(rows, key);
    const range = ctx.liveAxisRanges?.[axis] ?? dataExtentRange(rows, key);
    // A zoom reported one endpoint at a time can hold a NaN until the other lands.
    const span = range ? Math.abs(range[1] - range[0]) : 0;
    if (pitch === undefined || !Number.isFinite(span) || span <= 0) continue;
    const px = (pitch * length) / span;
    side = side === undefined ? px : Math.min(side, px);
  }
  return side;
}

export const spatialCategoricalChart: ChartTypeDefinition = {
  id: 'spatialCategorical',
  label: 'Spatial Categorical',
  capabilities: {
    multiSeries: false,
    supportsXKey: true,
    supportsYKey: true,
    supportsGroupBy: true,
    supportsLimits: false,
    filterable: true,
    supportsStacked: false,
    legendByDefault: false,
  },
  buildTraces(model, ctx) {
    const { data, unfilteredData, encoding, groupColors, options } = model;
    const yKey = encoding.yKey ?? 'y';
    const groupKey = encoding.groupKey ?? 'category';
    // Scaled with the figure, same as scatter — see ../theme/scale.
    const wantedSize = (options?.markerSize ?? DEFAULT_MARKER_SIZE) * figureScale(ctx.figureWidth);
    // Marker sizes are screen px; the blocks are a grid in data units. Capped at the
    // cell so adjacent blocks touch instead of covering each other (Bug 2720), with a
    // 1px floor — below that the screen cannot resolve the blocks anyway.
    // Only under `equalAspect` (which `squareAxes` overrides, as in makeLayout): its
    // explicit whole-dataset ranges are the spans the fit assumes.
    const fits = options?.fitMarkerToCell && options.equalAspect && !options.squareAxes;
    const cell = fits ? cellSidePx(model, ctx, yKey) : undefined;
    const markerSize = cell === undefined ? wantedSize : Math.max(1, Math.min(wantedSize, cell));
    // Square imitates a block cell in a plan view; default circle for scatter.
    const symbol = options?.markerSymbol ?? 'circle';

    // Resolve the x channel once; absent xKey falls back to the row index.
    const xValues = xColumn(data, encoding.xKey);
    // Trace order = z-order (later draws on top). Honor an explicit `groupOrder`
    // (background classes first), then any remaining categories in data order.
    //
    // The category set is every category the consumer DECLARED (`groupColors`), plus
    // any the data carries that it didn't — not just the ones present in the rows on
    // screen. Two things drove this, both about a legend that must not change between
    // levels: its size moves the plot area (and with it the axes, under the 1:1
    // constraint), and its content is what a reader compares one map against another
    // by. A declared class with no rows anywhere still earns its entry, at zero;
    // without it, "no false positives here" is indistinguishable from "this class
    // doesn't apply to this map".
    //
    // This is the one type where `groupColors` defines the set rather than only
    // colouring it — box and pie still take theirs from the data.
    const inData = uniqueValues(unfilteredData ?? data, groupKey);
    const declared = Object.keys(groupColors);
    const found = [...declared, ...inData.filter((c) => !declared.includes(c))];
    // Trace order is Z-ORDER, from `groupOrder`.
    const ordered = options?.groupOrder
      ? [...options.groupOrder.filter((c) => found.includes(c)), ...found.filter((c) => !options.groupOrder!.includes(c))]
      : found;
    // …and the legend reads in its own order, via `legendrank`. Plotly sorts legend
    // entries by rank while leaving the drawing order alone, so the backdrop class can
    // go down first without having to be listed first.
    const legendOrder = options?.legendOrder;

    // Optional "— N" count suffix on each legend entry, resolved per shown level by
    // normalizeConfig (see charts/shared/levelView).
    const counts = options?.showLegendCounts ? options.legendCounts : undefined;
    const legendName = (cat: string) => (counts?.[cat] != null ? `${cat} — ${counts[cat]}` : cat);

    return ordered.map((cat, i): ChartTrace => {
      const idx = data.map((_, j) => j).filter((j) => String(data[j][groupKey]) === cat);
      // A category with no rows on this level is carried as a single null point, not
      // as an empty array: Plotly draws no legend entry at all for a trace with zero
      // points, which is the size change this is here to avoid. A null plots nothing
      // and is ignored by autorange, so the entry appears and the map stays clean.
      const empty = idx.length === 0;
      return {
        x: empty ? [null] : idx.map((j) => xValues[j]),
        y: empty ? [null] : idx.map((j) => data[j][yKey]),
        mode: 'markers',
        type: 'scatter',
        name: legendName(cat),
        marker: { color: groupColors[cat] || colorAt(i, ctx.palette), size: markerSize, symbol },
        ...(legendOrder?.includes(cat) ? { legendrank: legendOrder.indexOf(cat) + 1 } : {}),
      };
    });
  },
  buildLayout() {
    return { margin: { t: 20, r: 20, l: 50, b: 50 } };
  },
};

registerChart(spatialCategoricalChart);
